import React, { useState, useEffect } from "react";
import { apiFetch } from "../../services/api";
import ReportTypeSelection from "./wizard/ReportTypeSelection";
import BasicConfiguration from "./wizard/BasicConfiguration";
import RoadmapConfiguration from "./wizard/RoadmapConfiguration";
import DataQualityConfiguration from "./wizard/DataQualityConfiguration";
import RelationshipConfiguration from "./wizard/RelationshipConfiguration";
import PreviewStep from "./wizard/PreviewStep";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();

const ReportBuilderWizard = ({ user, onClose, editingReport = null }) => {
  const isEditMode = !!editingReport;

  // When editing, start at step 2 (skip type selection since it can't change)
  const [currentStep, setCurrentStep] = useState(isEditMode ? 2 : 1);
  const [reportData, setReportData] = useState({
    report_type: null,
    name: "",
    entity_type: "",
    description: "",
    configuration: {},
    is_shared: false,
    category: "",
    tags: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Pre-populate form when editing
  useEffect(() => {
    if (editingReport) {
      setReportData({
        report_type: editingReport.report_type,
        name: editingReport.name || "",
        entity_type: editingReport.entity_type || "",
        description: editingReport.description || "",
        configuration: editingReport.configuration || {},
        is_shared: editingReport.is_shared || false,
        category: editingReport.category || "",
        tags: editingReport.tags || [],
      });
    }
  }, [editingReport]);

  const totalSteps = 4;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    // When editing, don't go back to step 1 (type selection)
    const minStep = isEditMode ? 2 : 1;
    if (currentStep > minStep) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleStepDataChange = (stepData) => {
    setReportData((prev) => ({
      ...prev,
      ...stepData,
    }));
  };

  const handleSave = async (saveAndRun = false) => {
    setSaving(true);
    setError(null);

    try {
      // Validate required fields
      if (!reportData.name.trim()) {
        throw new Error("Report name is required");
      }
      if (!reportData.report_type) {
        throw new Error("Report type is required");
      }

      let response;
      if (isEditMode) {
        // Update existing report
        response = await apiFetch(`/api/reports/${editingReport.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reportData),
        });
      } else {
        // Create new report
        response = await apiFetch("/api/reports", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reportData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Failed to ${isEditMode ? "update" : "create"} report`
        );
      }

      const result = await response.json();

      // For "Save & Run", pass the report with type info so dashboard can open the viewer
      if (saveAndRun && result.report) {
        // Ensure the report has the type information needed for routing
        const reportWithType = {
          ...result.report,
          report_type: result.report.report_type || reportData.report_type,
        };
        onClose(true, reportWithType);
      } else {
        // Just saved, return to dashboard
        onClose(true, null);
      }
    } catch (err) {
      console.error("Error saving report:", err);
      setError(
        err.message || `Failed to ${isEditMode ? "update" : "create"} report`
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ReportTypeSelection
            selectedType={reportData.report_type}
            onSelect={(type) => handleStepDataChange({ report_type: type })}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <BasicConfiguration
            data={reportData}
            onChange={handleStepDataChange}
            onNext={handleNext}
            onBack={isEditMode ? () => onClose(false) : handleBack}
            isEditMode={isEditMode}
            reportType={reportData.report_type}
          />
        );
      case 3:
        if (reportData.report_type === "roadmap") {
          return (
            <RoadmapConfiguration
              data={reportData}
              onChange={handleStepDataChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          );
        } else if (reportData.report_type === "data-quality") {
          return (
            <DataQualityConfiguration
              data={reportData}
              onChange={handleStepDataChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          );
        } else if (reportData.report_type === "relationship") {
          return (
            <RelationshipConfiguration
              data={reportData}
              onChange={handleStepDataChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          );
        }
        return null;
      case 4:
        return (
          <PreviewStep
            data={reportData}
            onSave={handleSave}
            onBack={handleBack}
            saving={saving}
            isEditMode={isEditMode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {isEditMode
                ? `Edit Report: ${editingReport.name}`
                : "Create New Report"}
            </h1>
            <p className="text-gray-400 text-sm">
              Step {currentStep} of {totalSteps}
              {isEditMode && " (Type cannot be changed)"}
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`flex-1 h-2 rounded-full ${
                  step <= currentStep ? "bg-blue-600" : "bg-gray-700"
                } ${step < currentStep ? "mr-2" : step > currentStep ? "ml-2" : ""}`}
              />
            ))}
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Type</span>
            <span>Basic Info</span>
            <span>Configuration</span>
            <span>Preview</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
            {error}
          </div>
        )}

        {/* Step Content */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default ReportBuilderWizard;
